import { deg, fixed, trim } from '../../math/format';
import { chooseFunction, solveAngle, solveSide, type Side } from '../../math/geometry';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

const SIDE_WORD: Record<Side, string> = {
  opp: 'opposite',
  adj: 'adjacent',
  hyp: 'hypotenuse',
};

/*
 * 45° is deliberately absent. At 45° the opposite and adjacent are equal and
 * tan is 1, which collapses two of this topic's distractors onto the correct
 * answer — a question with two right-looking options teaches nothing.
 */
const ANGLES = [15, 20, 25, 30, 35, 40, 50, 55, 60] as const;

export const TRIG_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'trig.nameSide',
    topicId: 'trig',
    skill: 'Naming the sides from an angle',
    gen: (rng) => {
      const angle = rng.pick([30, 45, 60]);
      const side = rng.pick(['opp', 'adj', 'hyp'] as const);

      return {
        kind: 'tri',
        angleLabel: `${angle}°`,
        answerSide: side,
        prompt: `Tap the ${SIDE_WORD[side]} side.`,
        sub: `Work from the ${angle}° angle.`,
        why: 'Opposite is the side that does not touch the angle. Adjacent leans on it. The hypotenuse is always across from the square corner, whatever way the drawing is turned.',
        steps: [
          { kind: 'note', text: 'Stand at the marked angle and look across the triangle.' },
          { kind: 'compute', text: 'The side you are looking straight at is the opposite. The one under your feet is the adjacent.' },
          { kind: 'result', text: 'The hypotenuse never touches the square corner — it runs away from it.' },
        ],
      };
    },
  },
  {
    id: 'trig.findSide',
    topicId: 'trig',
    skill: 'Finding a side from an angle and a side',
    gen: (rng) => {
      const angle = rng.pick(ANGLES);
      const [have, want] = rng.pick([
        ['hyp', 'opp'],
        ['hyp', 'adj'],
        ['adj', 'opp'],
        ['opp', 'adj'],
      ] as const);
      const length = rng.step(1, 4, 0.25);
      const solved = solveSide(angle, have, length, want);
      const fn = chooseFunction(have, want).value;
      const correct = `${fixed(solved.value, 4)}"`;

      // The classic error is using the reciprocal — multiplying where you
      // should divide. That is what this distractor is.
      const flipped = (length * length) / solved.value;

      return {
        kind: 'mc',
        prompt: `Angle is ${angle}°, the ${SIDE_WORD[have]} side is ${trim(length, 3)}". How long is the ${SIDE_WORD[want]} side?`,
        formula: { sin: 'sin = opp ÷ hyp', cos: 'cos = adj ÷ hyp', tan: 'tan = opp ÷ adj' }[fn],
        why: `${fn} ${angle}° ties those two sides together. ${solved.steps[2]!.text.replace(/^\w+ = /, '')} = ${fixed(solved.value, 4)}".`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: `${fixed(flipped, 4)}"`,
            trap: `You went the wrong way with the ratio — multiplied where you needed to divide, or the reverse.`,
          },
          {
            label: `${fixed(solveSide(90 - angle, have, length, want).value, 4)}"`,
            trap: `That is the answer for ${90 - angle}°, the other angle in the triangle. Check which one the question gave you.`,
          },
          {
            label: `${fixed(length, 4)}"`,
            trap: 'That is the side you were given, not the one asked for.',
          },
        ]),
      };
    },
  },
  {
    id: 'trig.findAngle',
    topicId: 'trig',
    skill: 'Finding an angle from rise and run',
    gen: (rng) => {
      const run = rng.pick([1.0, 1.5, 2.0, 2.5, 3.0]);
      const rise = rng.step(0.125, run * 0.9, 0.125);
      const solved = solveAngle('opp', rise, 'adj', run);
      const correct = deg(solved.value);

      return {
        kind: 'mc',
        prompt: `A surface rises ${trim(rise, 3)}" over ${trim(run, 3)}" of run. What is the angle?`,
        formula: 'angle = tan⁻¹(opp ÷ adj)',
        why: `Rise and run are opposite and adjacent, so that is tangent. tan⁻¹(${fixed(rise / run, 4)}) = ${deg(solved.value)}.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: deg(90 - solved.value),
            trap: 'That is the other angle in the triangle. You solved for the corner you were not asked about.',
          },
          {
            label: fixed(rise / run, 2),
            trap: 'That is the ratio, not the angle. You still have to hit the inverse tangent key.',
          },
          {
            label: deg(solveAngle('opp', run, 'adj', rise).value),
            trap: 'Rise and run are swapped. Opposite goes on top.',
          },
        ]),
      };
    },
  },
  {
    id: 'trig.pickFunction',
    topicId: 'trig',
    skill: 'Choosing sine, cosine or tangent',
    gen: (rng) => {
      const [have, want] = rng.pick([
        ['adj', 'hyp'],
        ['opp', 'hyp'],
        ['adj', 'opp'],
        ['hyp', 'opp'],
        ['hyp', 'adj'],
        ['opp', 'adj'],
      ] as const);
      const solved = chooseFunction(have, want);
      const names = { sin: 'Sine', cos: 'Cosine', tan: 'Tangent' };
      const definitions = { sin: 'sin = opp ÷ hyp', cos: 'cos = adj ÷ hyp', tan: 'tan = opp ÷ adj' };
      const wrong = (['sin', 'cos', 'tan'] as const).filter((f) => f !== solved.value);

      return {
        kind: 'mc',
        prompt: `You know the ${SIDE_WORD[have]} side and want the ${SIDE_WORD[want]}. Which function?`,
        formula: definitions[solved.value],
        why: `${definitions[solved.value]} — that is the only one with both of those sides in it.`,
        steps: solved.steps,
        ...mcOptions(rng, names[solved.value], [
          ...wrong.map((f) => ({
            label: names[f],
            trap: `${definitions[f]} — that ratio does not contain both the sides you are working with.`,
          })),
          {
            label: 'Pythagoras only',
            trap: 'Pythagoras needs two sides and gives you no angle. Here you have an angle to work from.',
          },
        ]),
      };
    },
  },
];
